// src/components/hotel/RoomList.jsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './css/RoomList.module.css';
import hotelImagePlaceholder from '../../../../assets/hoteldefault.avif';

const buildImageUrl = (image) => {
    if (!image) return hotelImagePlaceholder;
    const cleaned = String(image).replace(/\\/g, '/');
    if (cleaned.startsWith('http')) return cleaned;
    return `${cleaned}`;
};

/* Skeleton shimmer pendant le chargement d'une image */
const Img = ({ src, alt, className, ...rest }) => {
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);

    const handleError = (e) => {
        e.target.onerror = null;
        setFailed(true);
        setLoaded(true);
    };

    return (
        <div className={`${styles.imgWrap} ${loaded ? styles.imgLoaded : ''}`}>
            {!loaded && <span className={styles.imgSkeleton} aria-hidden="true" />}
            <img
                src={failed ? hotelImagePlaceholder : src}
                alt={alt}
                onLoad={() => setLoaded(true)}
                onError={handleError}
                className={className}
                {...rest}
            />
        </div>
    );
};

const amenityIcons = {
    'Wi-Fi': 'fa-wifi',
    'Pool': 'fa-swimming-pool',
    'Spa': 'fa-spa',
    'Gym': 'fa-dumbbell',
    'Parking': 'fa-parking',
    'Restaurant': 'fa-cutlery',
    'Bar': 'fa-glass-martini',
    'Room Service': 'fa-concierge-bell',
    'Laundry': 'fa-shirt',
    'Air Conditioning': 'fa-snowflake-o',
    'TV': 'fa-television',
    'Breakfast': 'fa-coffee',
    'Pet Friendly': 'fa-paw',
    'Beach Access': 'fa-umbrella-beach',
    'Concierge': 'fa-bell',
    'Business Center': 'fa-briefcase',
    'Airport Shuttle': 'fa-bus',
    'Kitchen': 'fa-cutlery',
    'Balcony': 'fa-building-o',
    'Fireplace': 'fa-fire',
};

const getAmenityIcon = (amenity) => {
    return amenityIcons[amenity] || 'fa-check-circle';
};

const RoomList = ({ rooms, onSelectRoom, isVerifyingSession, selectedRoomId }) => {
    return (
        <div className={styles.roomList}>
            {rooms?.length > 0 ? (
                rooms.map((room) => (
                    <RoomCard
                        key={room._id}
                        room={room}
                        onSelectRoom={onSelectRoom}
                        isVerifying={isVerifyingSession && selectedRoomId === room._id}
                    />
                ))
            ) : (
                <div className={styles.noRooms}>
                    <div className={styles.noRoomsIcon}><i className="fa fa-bed" /></div>
                    <h3>No rooms available</h3>
                    <p>Please check back later for availability</p>
                </div>
            )}
        </div>
    );
};

const RoomCard = ({ room, onSelectRoom, isVerifying }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isImageFullscreen, setIsImageFullscreen] = useState(false);

    const nextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prevIndex) =>
            prevIndex === room.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? room.images.length - 1 : prevIndex - 1
        );
    };

    const openFullscreen = (e) => {
        e.stopPropagation();
        setIsImageFullscreen(true);
    };

    const closeFullscreen = () => {
        setIsImageFullscreen(false);
    };

    const isAvailable = room.numAvailable > 0;

    return (
        <>
            <div className={styles.roomCard}>
                {/* Image */}
                <div className={styles.roomImageSlider}>
                    {room.images?.length > 0 ? (
                        <>
                            <Img
                                src={buildImageUrl(room.images[currentImageIndex])}
                                alt={`${room.roomType} - Image ${currentImageIndex + 1}`}
                                className={styles.roomImage}
                                onClick={openFullscreen}
                            />

                            <span className={`${styles.roomBadge} ${isAvailable ? styles.roomBadgeAvailable : styles.roomBadgeBooked}`}>
                                {isAvailable ? 'Available' : 'Fully booked'}
                            </span>

                            {room.images.length > 1 && (
                                <>
                                    <button
                                        className={`${styles.sliderArrow} ${styles.sliderArrowLeft}`}
                                        onClick={prevImage}
                                        aria-label="Previous image"
                                    >
                                        <i className="fa fa-chevron-left" />
                                    </button>
                                    <button
                                        className={`${styles.sliderArrow} ${styles.sliderArrowRight}`}
                                        onClick={nextImage}
                                        aria-label="Next image"
                                    >
                                        <i className="fa fa-chevron-right" />
                                    </button>
                                    <div className={styles.imageIndicators}>
                                        {room.images.map((_, index) => (
                                            <button
                                                key={index}
                                                className={`${styles.indicatorDot} ${index === currentImageIndex ? styles.active : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentImageIndex(index);
                                                }}
                                                aria-label={`Go to image ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <Img
                            src={hotelImagePlaceholder}
                            alt="Default room"
                            className={styles.roomImage}
                            onClick={openFullscreen}
                        />
                    )}
                </div>

                {/* Détails */}
                <div className={styles.roomDetails}>
                    <div className={styles.roomHeader}>
                        <h3 className={styles.roomTitle}>{room.roomType}</h3>
                        <span className={styles.roomCapacity}>
                            <i className="fa fa-user-o" /> {room.capacity} {room.capacity > 1 ? 'guests' : 'guest'}
                        </span>
                    </div>

                    <p className={styles.roomDescription}>{room.description}</p>

                    <div className={styles.roomAmenities}>
                        {room.amenities?.slice(0, 4).map((amenity, index) => (
                            <span key={index} className={styles.amenityTag}>
                                <i className={`fa ${getAmenityIcon(amenity)}`} />
                                {amenity}
                            </span>
                        ))}
                        {room.amenities?.length > 4 && (
                            <span className={styles.amenityMore}>
                                +{room.amenities.length - 4} more
                            </span>
                        )}
                    </div>

                    <div className={styles.roomAvailability}>
                        <span className={styles.availabilityLabel}>
                            <i className="fa fa-hashtag" />
                            {room.numAvailable} of {room.maxAvailable} left
                        </span>
                    </div>
                </div>

                {/* Prix + CTA */}
                <div className={styles.roomPrice}>
                    <div className={styles.priceBlock}>
                        <div className={styles.priceAmount}>
                            XAF {room.pricePerNight.toLocaleString()}
                        </div>
                        <div className={styles.priceNight}>per night</div>
                    </div>
                    
                    <button
                        className={styles.bookButton}
                        disabled={!isAvailable || isVerifying}
                        onClick={() => onSelectRoom(room)}
                    >
                        {isVerifying ? (
                            <>
                                Verifying... <i className="fa fa-spinner fa-spin" />
                            </>
                        ) : isAvailable ? (
                            <>
                                Select Room <i className="fa fa-arrow-right" />
                            </>
                        ) : (
                            'Unavailable'
                        )}
                    </button>
                </div>
            </div>

            {/* Fullscreen Modal */}
            {isImageFullscreen && room.images?.length > 0 && createPortal(
                <div className={styles.fullscreenModal} onClick={closeFullscreen}>
                    <div className={styles.fullscreenContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closeFullscreen} onClick={closeFullscreen} aria-label="Close">
                            <i className="fa fa-times" />
                        </button>
                        <Img
                            src={buildImageUrl(room.images[currentImageIndex])}
                            alt={`${room.roomType} - Fullscreen view`}
                            className={styles.fullscreenImage}
                        />
                        {room.images.length > 1 && (
                            <>
                                <button
                                    className={`${styles.fullscreenArrow} ${styles.fullscreenArrowLeft}`}
                                    onClick={prevImage}
                                    aria-label="Previous image"
                                >
                                    <i className="fa fa-chevron-left" />
                                </button>
                                <button
                                    className={`${styles.fullscreenArrow} ${styles.fullscreenArrowRight}`}
                                    onClick={nextImage}
                                    aria-label="Next image"
                                >
                                    <i className="fa fa-chevron-right" />
                                </button>
                                <div className={styles.fullscreenIndicators}>
                                    {room.images.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`${styles.fullscreenIndicator} ${index === currentImageIndex ? styles.active : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCurrentImageIndex(index);
                                            }}
                                            aria-label={`Photo ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default RoomList;